import React from 'react';
import { useSearchParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import DomainBreadcrumbs from 'components/DomainBreadcrumbs';
import Circular from 'components/common/CustomCircular';
import { LanguageChip } from 'components/training';
import {
  ProgressDetailResult,
  ProgressSummaryResult,
  getProgressDetail,
  listAssignment,
  listProgressSummary,
} from 'stores/TrainingAdminStoreAdapter';
import { formatDateTime } from 'utils/DateTimeFormatter';
import { errorDefault } from 'utils/Toastify';

/** トップの軸（課題別 / ユーザー別） */
type Axis = 'assignment' | 'user';

/** ドリルダウンの階層 */
type View =
  | { level: 'all' }
  | { level: 'assignment'; assignmentId: string; assignmentName: string }
  | { level: 'user'; userId: string; userName: string }
  | {
      level: 'detail';
      userId: string;
      userName: string;
      assignmentId: string;
      assignmentName: string;
    };

/** ユーザー別トップの集計行 */
type UserOverview = { userId: string; userName: string; cleared: number; total: number };

const clearRate = (cleared: number | null, total: number | null): number => {
  if (!total || total === 0) return 0;
  return Math.round(((cleared ?? 0) / total) * 100);
};

/**
 * 進捗ダッシュボード（DEVELOPER）。
 * 全課題サマリ → 課題別/ユーザー別サマリ（ダイシング）→ 設問×言語詳細（ドリルダウン）。
 */
const ProgressDashboard: React.FC = () => {
  const { users } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // トップの軸（課題別 / ユーザー別）。クエリ ?axis= で初期値指定（既定 assignment）。
  const [axis, setAxis] = React.useState<Axis>(
    searchParams.get('axis') === 'user' ? 'user' : 'assignment',
  );
  // 進捗の表示フィルタ（未クリア=incomplete / 全て=all）。クエリ ?filter= で初期値指定（既定 incomplete）。
  const [filter, setFilter] = React.useState<'incomplete' | 'all'>(
    searchParams.get('filter') === 'all' ? 'all' : 'incomplete',
  );
  const [view, setView] = React.useState<View>({ level: 'all' });
  const [summaries, setSummaries] = React.useState<ProgressSummaryResult[]>([]);
  const [details, setDetails] = React.useState<ProgressDetailResult[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [expandedDetail, setExpandedDetail] = React.useState<Record<string, boolean>>({});
  // ユーザー別トップの集計
  const [userOverview, setUserOverview] = React.useState<UserOverview[]>([]);
  const [userOverviewLoading, setUserOverviewLoading] = React.useState(false);
  // 表示中（非表示でない）の課題ID集合。集計はこの集合に絞り、Home のウィジェットと一致させる。
  // null = 未取得（取得前は全件を許容し、取得後にフィルタする）。
  const [visibleAssignmentIds, setVisibleAssignmentIds] = React.useState<Set<string> | null>(null);

  const userName = React.useCallback(
    (userId: string) => users.find((u) => u.userId === userId)?.userName ?? userId,
    [users],
  );

  // 表示中の課題一覧を取得（includeHidden 既定 false のため非表示課題を含まない）
  React.useEffect(() => {
    listAssignment({})
      .then((list) => setVisibleAssignmentIds(new Set(list.map((a) => a.assignmentId))))
      .catch(() => setVisibleAssignmentIds(new Set()));
  }, []);

  // 表示中課題だけに絞る（未取得＝null のときは素通し）
  const isVisibleAssignment = React.useCallback(
    (assignmentId: string | null | undefined) =>
      !assignmentId || !visibleAssignmentIds || visibleAssignmentIds.has(assignmentId),
    [visibleAssignmentIds],
  );

  // URL クエリ（assignmentId / userId）から初期ビューを復元する。
  // - userId + assignmentId → 詳細（detail）
  // - assignmentId のみ → 課題別（assignment）
  // - userId のみ → ユーザー別（user）
  // グラフから «この課題» / «このユーザー» を指定して直接深い階層に飛べる。
  const initedRef = React.useRef(false);
  React.useEffect(() => {
    if (initedRef.current) return;
    const qAssignmentId = searchParams.get('assignmentId');
    const qUserId = searchParams.get('userId');
    const qAssignmentName = searchParams.get('assignmentName') ?? qAssignmentId ?? '';
    if (!qAssignmentId && !qUserId) {
      initedRef.current = true;
      return;
    }
    initedRef.current = true;
    if (qUserId && qAssignmentId) {
      setView({
        level: 'detail',
        userId: qUserId,
        userName: userName(qUserId),
        assignmentId: qAssignmentId,
        assignmentName: qAssignmentName,
      });
    } else if (qAssignmentId) {
      setAxis('assignment');
      setView({
        level: 'assignment',
        assignmentId: qAssignmentId,
        assignmentName: qAssignmentName,
      });
    } else if (qUserId) {
      setAxis('user');
      setView({ level: 'user', userId: qUserId, userName: userName(qUserId) });
    }
    // クエリは一度消費したら消す（戻る操作を素直にするため）
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, userName]);

  // サマリ取得（all / assignment / user）
  React.useEffect(() => {
    if (view.level === 'detail') return;
    // ユーザー別トップ（axis=user かつ level=all）は別集計を使うので個別取得しない
    if (view.level === 'all' && axis === 'user') return;

    // ユーザー配下（level=user）は、そのユーザーの「課題別」進捗を出す。
    // listProgressSummary({ userId }) は課題の全体件数を返すことがあるため、
    // 課題ごとの BY_ASSIGNMENT 行から当該ユーザーの行だけを抽出して
    // «そのユーザーの» クリア数 / 総設問数を確定させる（トップの集計と一致させる）。
    if (view.level === 'user') {
      const targetUserId = view.userId;
      let cancelled = false;
      setLoading(true);
      listProgressSummary({})
        .then((allRows) => {
          const assignmentIds = allRows
            .filter(
              (s) =>
                s.assignmentId &&
                (s.assignedUserCount ?? 0) > 0 &&
                isVisibleAssignment(s.assignmentId),
            )
            .map((s) => ({
              assignmentId: s.assignmentId as string,
              assignmentName: s.assignmentName ?? (s.assignmentId as string),
            }));
          if (assignmentIds.length === 0) return [] as ProgressSummaryResult[];
          return Promise.all(
            assignmentIds.map((a) =>
              listProgressSummary({ assignmentId: a.assignmentId })
                .then((rows) =>
                  rows
                    .filter((r) => r.userId === targetUserId)
                    .map((r): ProgressSummaryResult => ({
                      ...r,
                      mode: 'BY_USER',
                      assignmentId: a.assignmentId,
                      assignmentName: a.assignmentName,
                    })),
                )
                .catch((): ProgressSummaryResult[] => []),
            ),
          ).then((res) => res.flat());
        })
        .then((rows) => {
          if (!cancelled) setSummaries(rows);
        })
        .catch(() => errorDefault('進捗サマリの取得に失敗しました'))
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    const params = view.level === 'assignment' ? { assignmentId: view.assignmentId } : {};
    listProgressSummary(params)
      .then(setSummaries)
      .catch(() => errorDefault('進捗サマリの取得に失敗しました'))
      .finally(() => setLoading(false));
  }, [view, axis, isVisibleAssignment]);

  // ユーザー別トップの集計（課題ごとの per-user 行を横断合算して全体進捗率を出す）
  React.useEffect(() => {
    if (!(view.level === 'all' && axis === 'user')) return;
    let cancelled = false;
    setUserOverviewLoading(true);
    // まず全課題サマリ（ALL）を取得し、割当あり かつ 表示中の課題IDを得る（非表示は除外）
    listProgressSummary({})
      .then((allRows) => {
        const assignmentIds = allRows
          .filter(
            (s) =>
              s.assignmentId &&
              (s.assignedUserCount ?? 0) > 0 &&
              isVisibleAssignment(s.assignmentId),
          )
          .map((s) => s.assignmentId as string);
        if (assignmentIds.length === 0) return [] as ProgressSummaryResult[][];
        return Promise.all(
          assignmentIds.map((assignmentId) =>
            listProgressSummary({ assignmentId }).catch((): ProgressSummaryResult[] => []),
          ),
        );
      })
      .then((results) => {
        if (cancelled) return;
        const map = new Map<string, UserOverview>();
        results.flat().forEach((r) => {
          if (!r.userId) return;
          const e = map.get(r.userId) ?? {
            userId: r.userId,
            userName: r.userName ?? userName(r.userId),
            cleared: 0,
            total: 0,
          };
          e.cleared += r.clearedQuestionCount ?? 0;
          e.total += r.totalQuestionCount ?? 0;
          map.set(r.userId, e);
        });
        setUserOverview(
          Array.from(map.values()).sort(
            (a, b) => clearRate(a.cleared, a.total) - clearRate(b.cleared, b.total),
          ),
        );
      })
      .catch(() => errorDefault('進捗サマリの取得に失敗しました'))
      .finally(() => {
        if (!cancelled) setUserOverviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view, axis, userName, isVisibleAssignment]);

  // 詳細取得（drilldown）
  React.useEffect(() => {
    if (view.level !== 'detail') return;
    setLoading(true);
    setExpandedDetail({});
    getProgressDetail({ userId: view.userId, assignmentId: view.assignmentId })
      .then(setDetails)
      .catch(() => errorDefault('進捗詳細の取得に失敗しました'))
      .finally(() => setLoading(false));
  }, [view]);

  const breadcrumbs = (
    <Breadcrumbs sx={{ mb: 2 }}>
      {/* ルート: 軸に応じて「全課題 / 全ユーザー」。トップ以外はリンク */}
      <Link
        component="button"
        underline={view.level === 'all' ? 'none' : 'hover'}
        color={view.level === 'all' ? 'text.primary' : 'inherit'}
        onClick={() => setView({ level: 'all' })}
      >
        {axis === 'user' ? '全ユーザー' : '全課題'}
      </Link>

      {/* 中間 + 現在地。detail の親は「来た軸」に合わせる（課題別=課題 / ユーザー別=ユーザー） */}
      {view.level === 'assignment' && (
        <Typography color="text.primary">{view.assignmentName}</Typography>
      )}
      {view.level === 'user' && <Typography color="text.primary">{view.userName}</Typography>}

      {view.level === 'detail' &&
        (axis === 'user'
          ? [
              // 全ユーザー > [ユーザー] > 課題名
              <Link
                key="user"
                component="button"
                underline="hover"
                color="inherit"
                onClick={() =>
                  setView({ level: 'user', userId: view.userId, userName: view.userName })
                }
              >
                {view.userName}
              </Link>,
              <Typography key="assignment" color="text.primary">
                {view.assignmentName}
              </Typography>,
            ]
          : [
              // 全課題 > [課題] > ユーザー名
              <Link
                key="assignment"
                component="button"
                underline="hover"
                color="inherit"
                onClick={() =>
                  setView({
                    level: 'assignment',
                    assignmentId: view.assignmentId,
                    assignmentName: view.assignmentName,
                  })
                }
              >
                {view.assignmentName}
              </Link>,
              <Typography key="user" color="text.primary">
                {view.userName}
              </Typography>,
            ])}
    </Breadcrumbs>
  );

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
      <DomainBreadcrumbs
        items={[
          { label: '研修管理コンソール', to: '/training-admin' },
          { label: '進捗ダッシュボード' },
        ]}
      />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        {view.level !== 'all' && (
          <Link
            component="button"
            underline="none"
            onClick={() => {
              // detail の戻り先は「来た軸」に合わせる（課題別=課題 / ユーザー別=ユーザー）。それ以外はトップへ。
              if (view.level === 'detail') {
                setView(
                  axis === 'user'
                    ? { level: 'user', userId: view.userId, userName: view.userName }
                    : {
                        level: 'assignment',
                        assignmentId: view.assignmentId,
                        assignmentName: view.assignmentName,
                      },
                );
              } else {
                setView({ level: 'all' });
              }
            }}
            sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </Link>
        )}
        <Typography variant="h6" fontWeight={700}>
          進捗ダッシュボード
        </Typography>
        <Box sx={{ flex: 1 }} />
        {/* 進捗フィルタ切替（未クリア / 全て）。詳細以外で表示 */}
        {view.level !== 'detail' && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filter}
            onChange={(_e, v: 'incomplete' | 'all' | null) => {
              if (v !== null) setFilter(v);
            }}
            sx={{
              '& .MuiToggleButton-root': {
                px: 1.25,
                py: 0.25,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'none',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
              },
            }}
          >
            <ToggleButton value="incomplete">未クリア</ToggleButton>
            <ToggleButton value="all">全て</ToggleButton>
          </ToggleButtonGroup>
        )}
        {/* トップの軸切替（課題別 / ユーザー別）。トップ（level=all）でのみ表示 */}
        {view.level === 'all' && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={axis}
            onChange={(_e, v: Axis | null) => {
              if (v !== null) setAxis(v);
            }}
            sx={{
              '& .MuiToggleButton-root': {
                px: 1.25,
                py: 0.25,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'none',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
              },
            }}
          >
            <ToggleButton value="assignment">課題別</ToggleButton>
            <ToggleButton value="user">ユーザー別</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>
      {breadcrumbs}

      {loading ? (
        <Circular open />
      ) : view.level === 'detail' ? (
        <DetailView
          details={details}
          expanded={expandedDetail}
          onToggle={(qid) => setExpandedDetail((prev) => ({ ...prev, [qid]: !prev[qid] }))}
        />
      ) : view.level === 'all' && axis === 'user' ? (
        <UserOverviewList
          rows={userOverview}
          loading={userOverviewLoading}
          filter={filter}
          onSelect={(u) => setView({ level: 'user', userId: u.userId, userName: u.userName })}
        />
      ) : (
        <SummaryList
          view={view}
          summaries={
            // level 'all'（課題カード）/ 'user'（課題別カード）は非表示課題を除外して
            // Home ウィジェットと一致させる。'assignment'（ユーザーカード）は行に課題IDが無いため素通し。
            view.level === 'assignment'
              ? summaries
              : summaries.filter((s) => isVisibleAssignment(s.assignmentId))
          }
          userName={userName}
          filter={filter}
          onDrill={(summary) => {
            if (view.level === 'assignment' && summary.userId) {
              setView({
                level: 'detail',
                userId: summary.userId,
                userName: summary.userName ?? userName(summary.userId),
                assignmentId: view.assignmentId,
                assignmentName: view.assignmentName,
              });
            } else if (view.level === 'user' && summary.assignmentId) {
              setView({
                level: 'detail',
                userId: view.userId,
                userName: view.userName,
                assignmentId: summary.assignmentId,
                assignmentName: summary.assignmentName ?? summary.assignmentId,
              });
            } else if (view.level === 'all' && summary.assignmentId) {
              setView({
                level: 'assignment',
                assignmentId: summary.assignmentId,
                assignmentName: summary.assignmentName ?? summary.assignmentId,
              });
            }
          }}
        />
      )}
    </Box>
  );
};

// --- サマリ一覧 ---

type SummaryListProps = {
  view: View;
  summaries: ProgressSummaryResult[];
  userName: (userId: string) => string;
  filter: 'incomplete' | 'all';
  onDrill: (summary: ProgressSummaryResult) => void;
};

const SummaryList: React.FC<SummaryListProps> = ({
  view,
  summaries,
  userName,
  filter,
  onDrill,
}) => {
  const isUserCard = view.level === 'assignment'; // BY_ASSIGNMENT: ユーザーカード

  // 各行の進捗率を求め、フィルタ（未クリア=100%未満のみ / 全て）を適用する
  const rows = summaries
    .map((s) => {
      const resolvedUserName = s.userName ?? (s.userId ? userName(s.userId) : '');
      const primary =
        s.mode === 'ALL'
          ? (s.assignmentName ?? '')
          : s.mode === 'BY_ASSIGNMENT'
            ? resolvedUserName
            : (s.assignmentName ?? '');
      const rate =
        s.mode === 'ALL'
          ? Math.round((s.averageClearRate ?? 0) * 100)
          : clearRate(s.clearedQuestionCount, s.totalQuestionCount);
      const sub =
        s.mode === 'ALL'
          ? `${s.assignedUserCount ?? 0} 名に割当`
          : `${s.clearedQuestionCount ?? 0} / ${s.totalQuestionCount ?? 0} 問クリア`;
      return { s, primary, rate, sub };
    })
    .filter((r) => (filter === 'incomplete' ? r.rate < 100 : true));

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 3,
          p: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {filter === 'incomplete' ? '未クリアの項目はありません 🎉' : 'データがありません'}
        </Typography>
      </Box>
    );
  }
  return (
    <Grid container spacing={2}>
      {rows.map(({ s, primary, rate, sub }, i) => {
        const done = rate === 100;
        return (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`${primary}-${i}`}>
            <Paper
              elevation={0}
              onClick={() => onDrill(s)}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 3,
                p: 2,
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)' },
              }}
            >
              {/* リングとラベル */}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <ProgressRing value={rate} done={done} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
                    {isUserCard && (
                      <PersonIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                    )}
                    <Typography variant="subtitle2" fontWeight={600} noWrap>
                      {primary}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {sub}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
};

// --- ユーザー別トップ（各ユーザーの全体進捗率カード） ---

type UserOverviewListProps = {
  rows: UserOverview[];
  loading: boolean;
  filter: 'incomplete' | 'all';
  onSelect: (u: UserOverview) => void;
};

const UserOverviewList: React.FC<UserOverviewListProps> = ({ rows, loading, filter, onSelect }) => {
  if (loading) {
    return <Circular open />;
  }
  const visible =
    filter === 'incomplete' ? rows.filter((u) => clearRate(u.cleared, u.total) < 100) : rows;
  if (visible.length === 0) {
    return (
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 3, p: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {filter === 'incomplete'
            ? '未クリアの課題が残っているユーザーはいません 🎉'
            : '進捗データのあるユーザーがいません'}
        </Typography>
      </Box>
    );
  }
  return (
    <Grid container spacing={2}>
      {visible.map((u) => {
        const rate = clearRate(u.cleared, u.total);
        const done = rate === 100;
        return (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={u.userId}>
            <Paper
              elevation={0}
              onClick={() => onSelect(u)}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 3,
                p: 2,
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)' },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <ProgressRing value={rate} done={done} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
                    <PersonIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                    <Typography variant="subtitle2" fontWeight={600} noWrap>
                      {u.userName}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {u.cleared} / {u.total} 問クリア
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
};

/** 進捗率を表す小さな円形インジケータ（中央に %）。 */
const ProgressRing: React.FC<{ value: number; done: boolean }> = ({ value, done }) => (
  <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
    {/* トラック */}
    <CircularProgress
      variant="determinate"
      value={100}
      size={52}
      thickness={4}
      sx={{ color: 'action.hover' }}
    />
    {/* 実値 */}
    <CircularProgress
      variant="determinate"
      value={value}
      size={52}
      thickness={4}
      color={done ? 'success' : 'primary'}
      sx={{ position: 'absolute', left: 0 }}
    />
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography variant="caption" fontWeight={700} sx={{ fontSize: 12 }}>
        {value}%
      </Typography>
    </Box>
  </Box>
);

// --- 詳細（設問×言語） ---

type DetailViewProps = {
  details: ProgressDetailResult[];
  expanded: Record<string, boolean>;
  onToggle: (questionId: string) => void;
};

const DetailView: React.FC<DetailViewProps> = ({ details, expanded, onToggle }) => {
  if (details.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        この組み合わせの進捗データがありません
      </Typography>
    );
  }
  return (
    <Stack spacing={1.5}>
      {details.map((q) => (
        <Paper
          key={q.questionId}
          elevation={0}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            {q.title}
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {q.languages.map((lang) => (
              <LanguageChip
                key={lang.extension}
                extension={lang.extension}
                cleared={lang.cleared}
              />
            ))}
          </Stack>
          {q.languages
            .filter((l) => l.cleared)
            .map((lang) => (
              <Box key={lang.extension} sx={{ mt: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  <Typography variant="caption" color="text.secondary">
                    {lang.extension} クリア日時:{' '}
                    {lang.lastClearedAt ? formatDateTime(lang.lastClearedAt) : '-'}
                  </Typography>
                  <Link
                    component="button"
                    variant="caption"
                    onClick={() => onToggle(`${q.questionId}-${lang.extension}`)}
                  >
                    {expanded[`${q.questionId}-${lang.extension}`]
                      ? '検証ログ・解答を閉じる'
                      : '検証ログ・解答を見る'}
                  </Link>
                </Stack>
                {expanded[`${q.questionId}-${lang.extension}`] && (
                  <Box sx={{ mt: 1, pl: 3 }}>
                    {lang.answerDetailSnapshot && (
                      <>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          解答コード
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            mb: 1,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                            border: 1,
                            borderColor: 'divider',
                            fontSize: 11,
                            maxHeight: 200,
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                          }}
                        >
                          {lang.answerDetailSnapshot}
                        </Box>
                      </>
                    )}
                    {lang.verificationResults.length > 0 && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          検証ログ（クリア時）
                        </Typography>
                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                          {lang.verificationResults.map((v) => (
                            <Box
                              key={v.seq}
                              sx={{
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                px: 1,
                                py: 0.5,
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                #{v.seq} 引数: {v.arguments ?? '(なし)'} ・ {v.statusDescription}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            ))}
        </Paper>
      ))}
    </Stack>
  );
};

export default ProgressDashboard;
